import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Plus, Ruler } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import {
  emptySizeSetForm,
  fromSizeSet,
  toSizeSetCreate,
  toSizeSetUpdate,
} from '../api/size-set-mappers';
import { SizeSetDangerZone } from '../components/size-set-danger-zone';
import { SizeSetForm } from '../components/size-set-form';
import { useSizeSet } from '../hooks/use-size-set';
import { useCreateSizeSet, useUpdateSizeSet } from '../hooks/use-size-set-mutations';
import { useSizeSets } from '../hooks/use-size-sets';

const LIST = '/org/catalog-setup/size-sets';

function Rows() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12" />
      ))}
    </div>
  );
}

function SizeSetsList() {
  const { t } = useTranslation('sizeSets');
  const query = useSizeSets();
  const rows = query.data;

  // The tour points at the toolbar button; the empty state's copy of it carries no anchor.
  const newButton = (tour?: string) => (
    <Can permission="attributes.write">
      <Button asChild>
        <Link to="/org/catalog-setup/size-sets/new" data-tour={tour}>
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-muted-foreground max-w-prose text-sm">{t('description')}</p>
        {newButton('dictionary.size-sets.new-button')}
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !rows ? (
        <Rows />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Ruler}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton()}
          tour="dictionary.size-sets.empty"
          tourLink
        />
      ) : (
        <Card className="py-0" data-tour="dictionary.size-sets.table">
          <CardContent className="px-0">
            <Table aria-label={t('title')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.name')}</TableHead>
                  <TableHead>{t('columns.key')}</TableHead>
                  <TableHead>{t('columns.system')}</TableHead>
                  <TableHead>{t('columns.unit')}</TableHead>
                  <TableHead className="pr-6 text-right">{t('columns.sizes')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} aria-label={row.name}>
                    <TableCell className="pl-6 font-medium">
                      <Link
                        to="/org/catalog-setup/size-sets/$sizeSetId"
                        params={{ sizeSetId: row.id }}
                        className="hover:underline"
                      >
                        {row.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <code className="text-muted-foreground text-xs">{row.key}</code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">
                        {t(`system.${row.system}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {row.unit ?? '—'}
                    </TableCell>
                    <TableCell className="pr-6 text-right text-sm tabular-nums">
                      {t('sizeCount', { count: row.values.length })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** The catalog setup layout owns the page title, so an editor heads itself with an h2. */
function EditorHeader({
  title,
  description,
  meta,
}: {
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
}) {
  const { t } = useTranslation('sizeSets');
  return (
    <header className="flex flex-col gap-1 pb-4">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="text-muted-foreground -ml-2 h-7 w-fit px-2"
      >
        <Link to={LIST}>
          <ArrowLeft aria-hidden />
          {t('back')}
        </Link>
      </Button>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{title}</h2>
        {meta}
      </div>
      {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
    </header>
  );
}

function SizeSetCreate() {
  const { t } = useTranslation('sizeSets');
  const navigate = useNavigate();
  const create = useCreateSizeSet();
  return (
    <div>
      <EditorHeader title={t('new')} description={t('newDescription')} />
      <SizeSetForm
        mode="create"
        readOnly={false}
        defaultValues={emptySizeSetForm()}
        isSubmitting={create.isPending}
        onSubmit={async (values) => {
          const row = await create.mutateAsync(toSizeSetCreate(values));
          await navigate({
            to: '/org/catalog-setup/size-sets/$sizeSetId',
            params: { sizeSetId: row.id },
            replace: true,
          });
        }}
      />
    </div>
  );
}

function SizeSetEdit({ sizeSetId }: { sizeSetId: string }) {
  const { t } = useTranslation('sizeSets');
  const format = useFormat();
  const { can } = usePermissions();
  const query = useSizeSet(sizeSetId);
  const update = useUpdateSizeSet(sizeSetId);
  const router = useRouter();
  const sizeSet = query.data;

  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!sizeSet) return <Rows />;
  const readOnly = !can('attributes.write');

  return (
    <div>
      <EditorHeader
        title={sizeSet.name}
        meta={
          <Badge variant="secondary" className="font-normal">
            {t(`system.${sizeSet.system}`)}
          </Badge>
        }
        description={
          <span title={format.utc(sizeSet.updatedAt)}>
            {t('updatedAt', { when: format.relative(sizeSet.updatedAt) })} ·{' '}
            <code className="text-xs">{sizeSet.key}</code>
          </span>
        }
      />
      <SizeSetForm
        key={sizeSet.updatedAt}
        mode="edit"
        readOnly={readOnly}
        defaultValues={fromSizeSet(sizeSet)}
        isSubmitting={update.isPending}
        onSubmit={async (values) => {
          await update.mutateAsync(toSizeSetUpdate(values, sizeSet));
          // The crumb is loader data; a rename shows there only once the loader runs again.
          void router.invalidate();
        }}
        dangerZone={readOnly ? null : <SizeSetDangerZone sizeSet={sizeSet} />}
      />
    </div>
  );
}

/** Which screen of the tab: the list, the create editor, or one set's editor. */
export type SizeSetsView = 'list' | 'new' | { sizeSetId: string };

/**
 * Body of the Size sets tab and its detail routes, picked by `view`: the feature door exposes
 * this one page, so the `new` and `$sizeSetId` routes render it with their view.
 */
export function SizeSetsPage({ view = 'list' }: { view?: SizeSetsView }) {
  return (
    <FeatureErrorBoundary name="dictionary.sizeSets">
      {view === 'list' ? (
        <SizeSetsList />
      ) : view === 'new' ? (
        <SizeSetCreate />
      ) : (
        <SizeSetEdit key={view.sizeSetId} sizeSetId={view.sizeSetId} />
      )}
    </FeatureErrorBoundary>
  );
}
